'use client';

import React from 'react';
import WidgetHeader from 'src/components/Profiles/shared/widgets/WidgetHeader';
import { Card } from 'src/components/PulseUI';
import WidgetLoading from 'src/components/Profiles/shared/widgets/shared/WidgetLoading';
import WidgetEmpty from 'src/components/Profiles/shared/widgets/shared/WidgetEmpty';
import { useWidgetConfig } from 'src/context/providers/WidgetConfigProvider';
import { useProfile } from 'src/context/providers/ProfileProvider';
import {
  useGetCodeSetsVolumeQuery,
  useGetProviderVolumeQuery,
} from 'src/api/redux';
import { CLAIMS_LABEL, resolveClaimsMetric } from 'src/utils/encounterLabels';
import { handleNumericValue } from 'src/utils/handleNumericValue/handleNumericValue';
import { getEntityIdentifiers } from 'src/utils/getEntityIdentifiers';
import {
  applyDisplayMode,
  applyCaptureRate,
} from 'src/components/Profiles/shared/widgets/utils';
import { useFlags } from 'launchdarkly-react-client-sdk';
import MonthlyAvgBadge from 'src/components/Profiles/shared/widgets/MonthlyAvgBadge';
import CaptureRateBadge from 'src/components/Profiles/shared/widgets/CaptureRateBadge';
import {
  getBenchmarkState,
  HERO_COLOR,
} from 'src/components/Profiles/shared/widgets/shared/benchmark';
import TargetBadge from 'src/components/Profiles/shared/widgets/shared/TargetBadge';

const CompactTreatingVolume: React.FC = () => {
  const { entityDetails } = useProfile();
  const widgetConfig = useWidgetConfig();
  const widget = widgetConfig?.widget ?? null;
  const codeSetIds = [
    ...(widget?.code_sets ?? []),
    ...(widget?.code_set ? [widget.code_set] : []),
  ].map((cs: { id: number }) => cs.id);
  const encounterId = widget?.encounter?.id;
  const { providerId: entityId } = getEntityIdentifiers(entityDetails);
  const { showEncountersTotals } = useFlags();

  const minYear = widgetConfig?.minYear;
  const maxYear = widgetConfig?.maxYear;
  const dateRange =
    minYear != null || maxYear != null
      ? { gte: String(minYear), lte: String(maxYear) }
      : undefined;

  const useEncounter = codeSetIds.length === 0 && !!encounterId;

  const { data: codeSetsData, isLoading: codeSetsLoading } =
    useGetCodeSetsVolumeQuery(
      {
        entityId: entityId || '',
        params: {
          code_set_ids: codeSetIds,
          all_code_sets: false,
          ...(minYear != null && { min_year: minYear }),
          ...(maxYear != null && { max_year: maxYear }),
        },
      },
      { skip: !entityId || useEncounter || codeSetIds.length === 0 }
    );

  const { data: encounterData, isLoading: encounterLoading } =
    useGetProviderVolumeQuery(
      {
        providerId: entityId || '',
        cohort_id: encounterId ?? undefined,
        dateRange,
      },
      { skip: !entityId || !useEncounter }
    );

  const isQueryLoading = useEncounter ? encounterLoading : codeSetsLoading;

  // Cohort (encounter) slot: the claims-role value reads encounters when flagged, and
  // its label is resolved alongside it so a payload without `encounters` falls back to
  // claims *and* keeps the "Claims" label.
  const encounterMetric = resolveClaimsMetric(
    encounterData,
    true,
    !!showEncountersTotals
  );
  const claimsLabel = useEncounter ? encounterMetric.label : CLAIMS_LABEL;

  const data = useEncounter
    ? encounterData
      ? {
          total_claims: encounterMetric.value,
          total_patients: encounterData.patients,
          total_providers: encounterData.providers,
        }
      : undefined
    : codeSetsData;

  const hasData = !!data;
  const displayMode = widget?.display_mode;
  const captureRate = widget?.capture_rate;
  const isMonthlyAvg = displayMode === 'monthly_average';
  const isLoading = isQueryLoading || !!widgetConfig?.isLoading;
  const showProviders =
    !isLoading &&
    hasData &&
    !isMonthlyAvg &&
    data.total_providers != null &&
    !isNaN(Number(data.total_providers));

  const codeSets = codeSetsData?.code_sets ?? [];

  const volumeRows =
    codeSets.length > 0
      ? codeSets.map((cs, index) => ({
          key: cs.code_set?.id ?? index,
          label: cs.code_set?.label ?? `Code Set ${index + 1}`,
          claims: Number(cs.claims),
          patients: Number(cs.patients),
        }))
      : [
          {
            key: 'total',
            label: widget?.encounter?.label ?? 'Total',
            claims: Number(data?.total_claims),
            patients: Number(data?.total_patients),
          },
        ];

  const volumeType = widget?.volume_type;
  const showClaims = !volumeType || volumeType === 'claims';
  const showPatients = !volumeType || volumeType === 'patients';
  const isSingleCodeSet = volumeRows.length === 1 && hasData;

  const benchmark = widget?.benchmark ?? null;
  const benchmarkOperator = widget?.benchmark_operator ?? 'greater_than';

  const VALUE_CELL = 'py-2 text-right font-semibold text-teal-900 tabular-nums';

  const formatValue = (value: number) =>
    handleNumericValue(
      applyCaptureRate(applyDisplayMode(value, displayMode), captureRate)
    );

  const renderHero = () => {
    const row = volumeRows[0];
    const showPatientsAsHero = volumeType === 'patients';
    const heroRawValue = showPatientsAsHero ? row.patients : row.claims;
    const heroValue = formatValue(heroRawValue);
    const heroLabel = showPatientsAsHero ? 'Patients' : claimsLabel;

    // Only show the secondary metric when volume_type is not set (both are visible)
    const hasSubtitle = !volumeType;
    const subtitleRawValue = showPatientsAsHero ? row.claims : row.patients;
    const subtitleValue = formatValue(subtitleRawValue);
    const subtitleLabel = showPatientsAsHero ? claimsLabel : 'Patients';

    const heroNumeric = applyCaptureRate(
      applyDisplayMode(heroRawValue, displayMode),
      captureRate
    );
    const benchmarkState =
      benchmark !== null
        ? getBenchmarkState(heroNumeric, benchmark, benchmarkOperator)
        : null;
    const heroColor = benchmarkState
      ? HERO_COLOR[benchmarkState]
      : 'text-sky-600';

    return (
      <div
        className="flex flex-col gap-1"
        data-testid="compact-treating-volume-hero"
      >
        <div className="flex items-baseline gap-3">
          <span
            className={`text-4xl font-bold tabular-nums ${heroColor}`}
            data-testid="compact-treating-volume-hero-value"
            data-benchmark-state={benchmarkState ?? 'none'}
            aria-label={`${heroLabel}: ${heroValue}${
              benchmarkState ? `, ${benchmarkState} benchmark` : ''
            }`}
          >
            {heroValue}
          </span>
          <span
            className="text-sm font-semibold text-neutral-500"
            data-testid="compact-treating-volume-hero-label"
          >
            {heroLabel}
          </span>
        </div>
        {hasSubtitle && (
          <p
            className="text-sm text-neutral-500"
            data-testid="compact-treating-volume-hero-subtitle"
          >
            {subtitleValue} {subtitleLabel}
          </p>
        )}
      </div>
    );
  };

  const renderTable = () => (
    <div data-testid="compact-treating-volume-stats">
      <table className="w-full text-sm" aria-label="Treating volume stats">
        <thead>
          <tr className="border-b border-neutral-100">
            <th className="text-left py-2" />
            {showClaims && (
              <th className="text-right py-2 font-medium text-neutral-500 text-xs w-24">
                {claimsLabel}
              </th>
            )}
            {showPatients && (
              <th className="text-right py-2 font-medium text-neutral-500 text-xs w-24">
                Patients
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {volumeRows.map((row, index) => (
            <tr
              key={row.key}
              className={
                index < volumeRows.length - 1
                  ? 'border-b border-neutral-100'
                  : ''
              }
              data-testid={`compact-treating-volume-code-set-${row.key}`}
            >
              <td className="py-2 font-medium text-primary">{row.label}</td>
              {showClaims && (
                <td
                  className={VALUE_CELL}
                  data-testid={`compact-treating-volume-claims-${row.key}`}
                >
                  {formatValue(row.claims)}
                </td>
              )}
              {showPatients && (
                <td
                  className={VALUE_CELL}
                  data-testid={`compact-treating-volume-patients-${row.key}`}
                >
                  {formatValue(row.patients)}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <Card
      className="p-4 flex flex-col gap-2 bg-white h-full"
      data-testid="compact-treating-volume"
      role="region"
      aria-label={widget?.label ?? 'Treating Volume'}
    >
      <WidgetHeader
        fallbackTitle="Treating Volume"
        trailing={
          <div
            className="flex items-center gap-1.5"
            data-testid="compact-treating-volume-trailing"
          >
            {isMonthlyAvg && <MonthlyAvgBadge />}
            {captureRate != null && <CaptureRateBadge rate={captureRate} />}
            {isSingleCodeSet && benchmark !== null && (
              <TargetBadge
                target={benchmark}
                testId="compact-treating-volume-target-badge"
              />
            )}
            {showProviders && (
              <span
                className="text-xs text-secondary bg-neutral-100 rounded-full px-2.5 py-0.5 whitespace-nowrap"
                data-testid="compact-treating-volume-providers-badge"
              >
                <span
                  className="font-bold tabular-nums"
                  data-testid="compact-treating-volume-providers-value"
                >
                  {formatValue(Number(data.total_providers))}
                </span>{' '}
                Providers
              </span>
            )}
          </div>
        }
      />
      <div className={isSingleCodeSet ? '' : 'flex-1 min-h-0 overflow-y-auto'}>
        {isLoading ? (
          <WidgetLoading data-testid="compact-treating-volume-loading" />
        ) : !hasData ? (
          <WidgetEmpty data-testid="compact-treating-volume-empty" variant="full" message="No volume data available" />
        ) : isSingleCodeSet ? (
          renderHero()
        ) : (
          renderTable()
        )}
      </div>
    </Card>
  );
};

export default CompactTreatingVolume;
